import {useMutation, useQuery} from "@tanstack/react-query";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router";
import {ReactSVG} from "react-svg";
import {Button} from "semantic-ui-react";
import ManagementExpectSection from "../components/Home/ManagementExpectSection";
import AlertToaster from "../components/shared/AlertToaster";
import CustomHeader from "../components/shared/CustomHeader";
import {getSubdomain} from "../config/StylesheetInjector";
import imageRegistry from "../constants/imageRegistry";
import useYupSchema from "../hooks/useYupSchema";
import {useTrackSFOSteps} from "../react-query/Mutations";
import {getPersonalInfo,} from "../redux/slices/PersonalAccountInformationSlice";
import {skipOnboarding} from "../services/OnboardingSteps";
import {routerPaths} from "../utils/routerPaths";
import {isEmpty} from "lodash";
import {gaJSON, useTrackFirebaseAnalytics} from "../utils/googleAnalytics";
import {FETCH_VEHICLES} from "../react-query/QueryConstant";
import {getVehicles} from "../services/Vehicles";
import {identifyEvent, trackEvent} from "../utils/segmentAnalytics";
import {COUNTRY_CODE} from "../constants/form";

const Home = () => {
    const {t} = useTranslation("home");
    const dispatch = useDispatch();
    const {buildPersonalInfoSchema} = useYupSchema();
    const personalInfo = useSelector((state) => getPersonalInfo(state));
    const {mutateAsync: trackSFOSteps} = useTrackSFOSteps();

    const [isOnboardingStarted, setIsOnboardingStarted] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);
    const [wantToSkipOnboarding, setWantToSkipOnboarding] = useState(false);

    const {data: vehiclesData, isLoading} = useQuery({
        queryKey: [FETCH_VEHICLES, personalInfo.fleet_id],
        queryFn: () => getVehicles({fleetId: personalInfo.fleet_id}),
        enabled: !!personalInfo.fleet_id,
    });

    const MANAGEMENT_EXPECTATION = [
        {
            id: 1,
            header: t("vehicleDetails"),
            info: t("vinOrLicensePlateNumberStateForEachVehicle"),
            icon: imageRegistry.VehicleIcon,
        },
        {
            id: 2,
            header: t("driverDetailsOptional"),
            info: t("driverNameEmailMobileNumber"),
            icon: imageRegistry.DriverDetailsIcon
        }
    ];

    const sub = getSubdomain();
    const navigate = useNavigate();
    const {
        mutateAsync: skipOnboardingMutation,
        isPending: skipOnboardingLoadere,
    } = useMutation({
        mutationFn: () => {
            return skipOnboarding();
        },
    });

    const track = useTrackFirebaseAnalytics();
    useEffect(() => {
        if (personalInfo.id && vehiclesData?.result?.length) {
            track(gaJSON.onboarding_screen_viewed, {
                step_name: "welcome",
                card_count: vehiclesData?.result?.length
            })

            track(gaJSON.onboarding_started, {
                time: new Date(),
                card_count: vehiclesData?.result?.length
            })

        }
    }, [personalInfo, vehiclesData]);

    return (
        <div className="home">
            <div className="container">
                <div className="whilelabel-logo">
                    <ReactSVG src={imageRegistry[sub]}/>
                </div>
                {!isOnboardingStarted && (
                    <div className="home-title-margin">
                        <CustomHeader
                            className="home_expectation"
                            title={t("titleExpectation")}/>
                        <CustomHeader
                            className="home_subheader"
                            title={t("home_subheader")}
                        />
                    </div>
                )}
                {!isOnboardingStarted && (
                    <div className="expectation__section">
                        <CustomHeader
                            className="home_subheaderBold"
                            title={t("WhatYouNeedToGetStarted")}
                        />
                        {MANAGEMENT_EXPECTATION.map((expect, index) => (
                            <div className="home__expectation-section" key={expect.id}>
                                <ManagementExpectSection
                                    header={expect.header}
                                    info={expect.info}
                                    index={index + 1}
                                    icon={expect.icon}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {!isOnboardingStarted && (
                    <AlertToaster
                        containerClass="home__alert-toaster"
                        titleBold={t("assignAtLeastOneVehicleToCompleteOnboarding")}
                        titleNormal={t("andUnlockFullAccessToYourFuelmanMaintenanceAccount")}
                    />
                )}

                {!isOnboardingStarted && (
                    <Button
                        onClick={() => {
                            identifyEvent({
                                traits: {
                                    firstName: personalInfo.firstName,
                                    lastName: personalInfo.lastName,
                                    phone: COUNTRY_CODE.concat(personalInfo.cellPhone),
                                    zip: personalInfo.zip,
                                    email: personalInfo.email
                                }
                            })
                            trackEvent("Onboarding Started", {})
                            navigate(`${routerPaths.fleetAccountInfo}`)
                        }}
                        className="home__start-onboarding"
                        disabled={isEmpty(personalInfo)}
                    >
                        {t("startOnboarding")}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default Home;
